import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "./api";

const PirateContext = createContext(null);
const STORAGE_KEY = "desashop_pirate_v1";
const DEVICE_KEY = "desashop_device_id";

function getDeviceId() {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
        id =
            "d_" +
            Math.random().toString(36).slice(2, 10) +
            Date.now().toString(36);
        localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
}

// Fire-and-forget analytics event
function trackEvent(kind, state, meta) {
    try {
        api.post("/analytics/pirate-event", {
            device_id: getDeviceId(),
            kind,
            level: state.level,
            level_name: state.level_name,
            xp: state.xp,
            berry: state.berry,
            meta: meta || null,
        }).catch(() => {});
    } catch (_) {}
}

/**
 * Level tiers — each level requires cumulative XP
 * Names inspired by pirate hierarchy without infringing copyright
 */
export const LEVELS = [
    { level: 1, name: "Mozzo", threshold: 0, badge: "⚓" },
    { level: 2, name: "Marinaio", threshold: 100, badge: "🧭" },
    { level: 3, name: "Pirata", threshold: 300, badge: "☠" },
    { level: 4, name: "Cacciatore di Taglie", threshold: 600, badge: "🗡" },
    { level: 5, name: "Capitano", threshold: 1200, badge: "👑" },
    { level: 6, name: "Signore della Guerra", threshold: 2500, badge: "⚔" },
    { level: 7, name: "Imperatore", threshold: 5000, badge: "🔥" },
    { level: 8, name: "Re dei Pirati", threshold: 10000, badge: "🏴‍☠️" },
];

export const REWARDS = {
    firstVisit: { xp: 25, berry: 100 },
    viewProduct: { xp: 5, berry: 5 },
    addToCart: { xp: 20, berry: 25 },
    newsletter: { xp: 100, berry: 500 },
    purchase: { xp: 500, berry: 1000 },
    dailyVisit: { xp: 30, berry: 50 },
};

function loadState() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw);
    } catch (_e) {
        // fallthrough to defaults
    }
    return {
        xp: 0,
        berry: 0,
        actions: {}, // { productId: true } → prevents farming same product
        firstVisit: false,
        lastVisitDate: null,
    };
}

function getLevelFor(xp) {
    let current = LEVELS[0];
    for (const l of LEVELS) {
        if (xp >= l.threshold) current = l;
    }
    return current;
}

function getNextLevel(current) {
    return LEVELS.find((l) => l.level === current.level + 1) || null;
}

export function PirateProvider({ children }) {
    const [state, setState] = useState(loadState);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }, [state]);

    // First visit + daily visit bonuses
    useEffect(() => {
        const today = new Date().toISOString().slice(0, 10);
        setState((prev) => {
            const next = { ...prev };
            let toastMsg = null;
            let evtKind = null;
            if (!next.firstVisit) {
                next.xp += REWARDS.firstVisit.xp;
                next.berry += REWARDS.firstVisit.berry;
                next.firstVisit = true;
                toastMsg = `Benvenuto pirata! +${REWARDS.firstVisit.xp} XP · +${REWARDS.firstVisit.berry} Berry`;
                evtKind = "first_visit";
            } else if (next.lastVisitDate !== today) {
                next.xp += REWARDS.dailyVisit.xp;
                next.berry += REWARDS.dailyVisit.berry;
                toastMsg = `Bentornato! +${REWARDS.dailyVisit.xp} XP · +${REWARDS.dailyVisit.berry} Berry`;
                evtKind = "daily_visit";
            }
            next.lastVisitDate = today;
            if (toastMsg) {
                setTimeout(() => toast.success(toastMsg), 800);
            }
            if (evtKind) {
                const lvl = getLevelFor(next.xp);
                trackEvent(evtKind, {
                    level: lvl.level,
                    level_name: lvl.name,
                    xp: next.xp,
                    berry: next.berry,
                });
            }
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const level = useMemo(() => getLevelFor(state.xp), [state.xp]);
    const nextLevel = useMemo(() => getNextLevel(level), [level]);
    const progress = useMemo(() => {
        if (!nextLevel) return 100;
        const range = nextLevel.threshold - level.threshold;
        const gained = state.xp - level.threshold;
        return Math.min(100, Math.max(0, (gained / range) * 100));
    }, [state.xp, level, nextLevel]);

    const award = useCallback((kind, meta = {}) => {
        setState((prev) => {
            const reward = REWARDS[kind];
            if (!reward) return prev;

            // Idempotency for view/addToCart per product
            const actionKey = meta.productId
                ? `${kind}:${meta.productId}`
                : null;
            if (actionKey && prev.actions[actionKey] && kind === "viewProduct") {
                return prev; // already viewed
            }

            const newXp = prev.xp + reward.xp;
            const newBerry = prev.berry + reward.berry;
            const oldLevel = getLevelFor(prev.xp);
            const nextLvl = getLevelFor(newXp);
            const leveled = nextLvl.level > oldLevel.level;
            if (leveled) {
                setTimeout(() => {
                    toast.success(
                        `LEVEL UP! Ora sei ${nextLvl.badge} ${nextLvl.name}`,
                        { duration: 5000 }
                    );
                }, 400);
            } else if (kind !== "viewProduct") {
                setTimeout(() => {
                    toast(`+${reward.xp} XP · +${reward.berry} Berry`, {
                        duration: 2500,
                    });
                }, 200);
            }

            // Telemetry (non-blocking)
            trackEvent(kind, {
                level: nextLvl.level,
                level_name: nextLvl.name,
                xp: newXp,
                berry: newBerry,
            }, meta);
            if (leveled) {
                trackEvent("level_up", {
                    level: nextLvl.level,
                    level_name: nextLvl.name,
                    xp: newXp,
                    berry: newBerry,
                });
            }

            return {
                ...prev,
                xp: newXp,
                berry: newBerry,
                actions: actionKey
                    ? { ...prev.actions, [actionKey]: true }
                    : prev.actions,
            };
        });
    }, []);

    const track = useCallback((kind, meta) => {
        // Read current state fresh
        setState((prev) => {
            const lvl = getLevelFor(prev.xp);
            trackEvent(kind, {
                level: lvl.level,
                level_name: lvl.name,
                xp: prev.xp,
                berry: prev.berry,
            }, meta);
            return prev;
        });
    }, []);

    const spendBerry = useCallback((amount) => {
        let ok = false;
        setState((prev) => {
            if (prev.berry >= amount) {
                ok = true;
                return { ...prev, berry: prev.berry - amount };
            }
            return prev;
        });
        return ok;
    }, []);

    const reset = useCallback(() => {
        setState({
            xp: 0,
            berry: 0,
            actions: {},
            firstVisit: true,
            lastVisitDate: new Date().toISOString().slice(0, 10),
        });
    }, []);

    return (
        <PirateContext.Provider
            value={{
                xp: state.xp,
                berry: state.berry,
                level,
                nextLevel,
                progress,
                award,
                spendBerry,
                reset,
                track,
            }}
        >
            {children}
        </PirateContext.Provider>
    );
}

export const usePirate = () => useContext(PirateContext);

export function formatBerry(n) {
    return new Intl.NumberFormat("it-IT").format(Math.floor(n)) + " ฿";
}
